<!--
  This component is a carousel that allows the user to scroll horizontally through a list of items.
  It supports dragging to scroll, and it shows left and right arrows when the list is overflowing.
  [Attention] While dragging the child elements cant be clicked and the text selection is disabled.
 -->

<template>
  <div ref="outerContainer" class="outer-container">
    <div class="inner-container no-scroll" ref="innerContainer">
      <slot></slot>
    </div>
    <CarouselArrow
      direction="left"
      v-if="showArrows"
      :hide="scrolledCompletelyLeft"
      @click="scrollLeft"
    />
    <CarouselArrow
      direction="right"
      v-if="showArrows"
      :hide="scrolledCompletelyRight"
      @click="scrollRight"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, watch } from "vue"
import CarouselArrow from "app/ui-primitives/carousel/CarouselArrow.vue"
import { useDragToScroll } from "app/ui-primitives/carousel/use-drag-to-scroll"
import { useScrollPositionCheck } from "app/ui-primitives/carousel/use-scroll-position-check"
import { useOverflowCheck } from "app/ui-primitives/carousel/use-overflow-check"

export default defineComponent({
  name: "Carousel",
  components: { CarouselArrow },
  props: {
    dragToScroll: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const outerContainer = ref(null)
    const innerContainer = ref(null)
    const alreadyMounted = ref(false)

    const {
      isOverflowing: showArrows,
      registerOverflowCheck,
      unregisterOverflowCheck,
    } = useOverflowCheck(outerContainer, innerContainer)

    const {
      scrolledCompletelyLeft,
      scrolledCompletelyRight,
      registerScrollPositionCheck,
      unregisterScrollPositionCheck,
    } = useScrollPositionCheck(outerContainer, innerContainer)

    const {
      registerDragToScroll: _registerDragToScroll,
      unregisterDragToScroll: _unregisterDragToScroll,
    } = useDragToScroll(innerContainer)

    const registerDragToScroll = () => {
      if (props.dragToScroll) {
        _registerDragToScroll()
      }
    }

    const unregisterDragToScroll = () => {
      if (props.dragToScroll) {
        _unregisterDragToScroll()
      }
    }

    onMounted(() => {
      alreadyMounted.value = true
      registerOverflowCheck()
      registerScrollPositionCheck()
      registerDragToScroll()
    })

    onUnmounted(() => {
      unregisterOverflowCheck()
      unregisterScrollPositionCheck()
      unregisterDragToScroll()
    })

    /**
     * If the inner-container is not overflowing, we don't need to register the drag to scroll functionality.
     */
    watch(
      showArrows,
      (isOverflowing) => {
        if (isOverflowing && alreadyMounted.value) {
          registerDragToScroll()
        } else if (alreadyMounted.value) {
          unregisterDragToScroll()
        }
      },
      { immediate: true }
    )

    const scrollLeft = () => {
      const _innerContainer = innerContainer.value
      if (_innerContainer) {
        _innerContainer.scrollBy({
          left: -320,
          behavior: "smooth",
        })
      }
    }

    const scrollRight = () => {
      const _innerContainer = innerContainer.value
      if (_innerContainer) {
        _innerContainer.scrollBy({
          left: 320,
          behavior: "smooth",
        })
      }
    }

    return {
      outerContainer,
      innerContainer,
      showArrows,
      scrolledCompletelyLeft,
      scrolledCompletelyRight,
      scrollLeft,
      scrollRight,
    }
  },
})
</script>

<style scoped>
.outer-container {
  position: relative;
  overflow: visible;
}

.inner-container {
  display: flex;
  overflow-x: auto;
  padding-top: 16px;
  padding-bottom: 16px;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.no-scroll::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scroll {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
